import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import configService from '../services/configService';

function BusinessDescription({ onComplete }) {
  const { user, updateUserProfile } = useAuth();
  const [formData, setFormData] = useState({
    businessDescription: '',
    assistantName: ''
  });
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    loadExistingOrGenerate();
    
    // Debug function to force show form
    window.debugForceBusinessDescForm = () => {
      console.log('📝 DEBUG: Forcing business description form to show');
      setGenerating(false);
      setLoading(false);
      setError('');
      setFormData({
        businessDescription: 'Test business description',
        assistantName: 'Test Assistant'
      });
    };
    
    // Debug function to test new config flag
    window.debugTestNewConfigFlag = () => {
      console.log('📝 DEBUG: Setting isNewConfig flag and reloading');
      sessionStorage.setItem('isNewConfig', 'true');
      loadExistingOrGenerate();
    };
  }, []);

  // Expose save function to Setup.js
  useEffect(() => {
    window.saveBusinessDescription = async () => {
      try {
        console.log('💾 BusinessDescription: Saving via Setup.js');
        await updateBusinessConfig(formData);
        return true;
      } catch (error) {
        console.error('❌ BusinessDescription: Save failed:', error);
        setError('Failed to save business description. Please try again.');
        return false;
      }
    };
    
    return () => {
      delete window.saveBusinessDescription;
    };
  }, [formData]);
  
  const loadExistingOrGenerate = async () => {
    try {
      const cognitoUserId = user?.attributes?.sub;
      console.log('📝 DEBUG: loadExistingOrGenerate - cognitoUserId:', cognitoUserId);
      if (!cognitoUserId) return;
      
      // Check if this is a new config from Step 1
      const isNewConfig = sessionStorage.getItem('isNewConfig') === 'true';
      console.log('📝 DEBUG: isNewConfig flag:', isNewConfig);
      
      if (isNewConfig) {
        console.log('📝 DEBUG: New config detected, auto-generating description');
        sessionStorage.removeItem('isNewConfig'); // Clear flag
        generateBusinessDescription();
        return;
      }
      
      // Try to load existing config first
      console.log('📝 DEBUG: Loading existing config...');
      const config = await configService.getBusinessConfigByUserId(cognitoUserId);
      console.log('📝 DEBUG: Loaded config:', config);
      
      if (config && config.business_description) {
        console.log('📝 DEBUG: Using existing data');
        // Use existing data (assistant_name might be null, that's ok)
        setFormData({
          businessDescription: config.business_description,
          assistantName: config.assistant_name || config.business_name + ' Assistant' || 'Business Assistant'
        });
        return;
      }
      
      console.log('📝 DEBUG: No existing data, generating new description');
      // If no existing data, generate new description
      generateBusinessDescription();
    } catch (error) {
      console.warn('📝 DEBUG: Could not load existing config, generating new:', error);
      generateBusinessDescription();
    }
  };

  const generateBusinessDescription = async () => {
    setGenerating(true);
    setError('');
    
    try {
      // Load business config from backend
      const cognitoUserId = user?.attributes?.sub;
      if (!cognitoUserId) {
        throw new Error('User not authenticated');
      }
      
      const config = await configService.getBusinessConfigByUserId(cognitoUserId);
      if (!config || !config.business_name) {
        throw new Error('Missing required business information');
      }
      
      // Generate description using the config server
      const response = await generateDescription();
      
      setFormData(prev => ({
        ...prev,
        businessDescription: response.generated_description || 'A professional business providing quality services.',
        assistantName: config.business_name + ' Assistant'
      }));
      
    } catch (error) {
      console.error('Error generating description:', error);
      setError('Failed to generate business description. Please enter manually.');
      setFormData(prev => ({
        ...prev,
        businessDescription: 'A professional business providing quality services.',
        assistantName: 'Business Assistant'
      }));
    } finally {
      setGenerating(false);
    }
  };



  const generateDescription = async () => {
    return await configService.generateBusinessDescription();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      // Update the config with the final description and assistant settings
      await updateBusinessConfig(formData);
      
      onComplete();
    } catch (error) {
      console.error('Error saving business description:', error);
      setError('Failed to save business description. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const updateBusinessConfig = async (data) => {
    console.log('💾 BusinessDescription: updateBusinessConfig called with:', data);
    return await configService.updateBusinessDescription(
      data.businessDescription,
      data.assistantName
    );
  };

  if (generating) {
    return (
      <div style={{
        background: 'white',
        padding: '2rem',
        borderRadius: '12px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
        maxWidth: '600px',
        margin: '0 auto',
        textAlign: 'center'
      }}>
        <div style={{ marginBottom: '1rem' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #f3f4f6',
            borderTop: '4px solid #2563eb',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1rem'
          }}></div>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
          <h3>Generating Business Description</h3>
          <p style={{ color: '#6b7280' }}>
            Using AI to analyze your business information and create a professional description...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      background: 'white',
      padding: '2rem',
      borderRadius: '12px',
      boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
      maxWidth: '600px',
      margin: '0 auto'
    }}>
      {error && (
        <div style={{
          background: '#fee2e2',
          color: '#dc2626',
          padding: '1rem',
          borderRadius: '6px',
          marginBottom: '1rem'
        }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
            <label style={{ fontWeight: '500', marginRight: '0.5rem' }}>
              Business Description (auto generated)
            </label>
            {!isEditing && (
              <button
                type="button"
                onClick={() => setIsEditing(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.25rem',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  color: '#6b7280',
                  fontSize: '0.9rem'
                }}
                title="Edit description"
              >
                ✏️ Edit
              </button>
            )}
          </div>
          
          {isEditing ? (
            <textarea
              value={formData.businessDescription}
              onChange={(e) => setFormData({...formData, businessDescription: e.target.value})}
              placeholder="Describe your business..."
              rows={4}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '1rem',
                resize: 'vertical'
              }}
              required
              autoFocus
            />
          ) : (
            <div
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                fontSize: '1rem',
                backgroundColor: '#f9fafb',
                minHeight: '100px',
                lineHeight: '1.5',
                cursor: 'pointer'
              }}
              onClick={() => setIsEditing(true)}
            >
              {formData.businessDescription || 'Click to add business description...'}
            </div>
          )}
          
          <p style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.25rem' }}>
            This description helps your AI assistant understand your business better.
          </p>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
            Assistant Name
          </label>
          <input
            type="text"
            value={formData.assistantName}
            onChange={(e) => setFormData({...formData, assistantName: e.target.value})}
            placeholder="e.g., Sarah, Business Assistant"
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '1rem'
            }}
            required
          />
        </div>



        <div style={{ display: 'flex', gap: '1rem' }}>
          <button
            type="button"
            onClick={generateBusinessDescription}
            disabled={generating}
            style={{
              width: '100%',
              padding: '0.75rem',
              background: generating ? '#9ca3af' : '#6b7280',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '1rem',
              cursor: generating ? 'not-allowed' : 'pointer'
            }}
          >
            {generating ? 'Generating...' : 'Regenerate Description'}
          </button>
          {isEditing && (
            <button
              type="button"
              onClick={() => setIsEditing(false)}
              style={{
                padding: '0.75rem 1rem',
                background: '#f3f4f6',
                color: '#374151',
                border: 'none',
                borderRadius: '6px',
                fontSize: '1rem',
                cursor: 'pointer'
              }}
            >
              Done
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

export default BusinessDescription;